import { useEffect, useState } from "react";
import {
  EXTENSION_OPEN_HASH,
  EXTENSION_SIGN_IN_PATH,
} from "../pixelmixer-webapp/src/service/ChromeExtensionService";
import { datadogRum } from "@datadog/browser-rum";
import { datadogLogs } from "@datadog/browser-logs";
import { useRouter } from "next/router";
import {
  logError,
  logInfo,
} from "../pixelmixer-webapp/src/service/ServiceUtil";
import {
  getQueryParams,
  isPixelMixerAccount,
  getIsTestAgent,
  isTestEnv,
} from "../pixelmixer-webapp/src/util/Utils";
import UserAgentUtil from "../pixelmixer-webapp/src/util/UserAgentUtil";
/*global jQuery*/
/*global window*/

export const useScript = ({
  src,
  async = true,
  type = "text/javascript",
  textContent,
}) => {
  useEffect(() => {
    if (!src) return;
    const script = document.createElement("script");

    script.src = src;
    script.async = true;
    script.textContent = textContent;
    script.type = type;

    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, [src]);
};

export const useAnalytics = () => {
  useEffect(() => {
    if (!getIsTestAgent()) {
      const script = document.createElement("script");
      script.src = "/static/js/analytics.js";
      document.body.appendChild(script);

      // return () => {
      //   document.body.removeChild(script);
      // };
    }
  }, []);
};

export const usePathname = () => {
  const [isMounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);
  const { pathname, hash = "" } = isMounted ? window.location : {};
  return pathname;
};

export const useWebsiteRoutePathname = () => {
  const [isMounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);

  const router = useRouter();
  const { pathname, hash = "" } = isMounted ? window.location : router;
  const queryParams = getQueryParams();
  const { redirect } = isMounted ? queryParams : router.query;

  const isSupportedPath = (path = "") =>
    path.startsWith(EXTENSION_SIGN_IN_PATH) ||
    path.startsWith("/slackUser") ||
    path.startsWith("/slackKeywordAlerts") ||
    path.startsWith("/slackChannel") ||
    path.startsWith("/myProfile/preferences") ||
    path.startsWith("/myProfile/keywords") ||
    path.startsWith("/myProfile/integrations") ||
    path.startsWith("/zoomBot") ||
    path.startsWith("/zoomLink");

  return hash.startsWith(EXTENSION_OPEN_HASH)
    ? hash
    : isSupportedPath(pathname)
      ? pathname
      : isSupportedPath(redirect)
        ? redirect
        : "/";
};

export const useSourceForgeReviews = () => {
  const setupTags = (variantId) => {
    const sc = document.createElement("script");
    sc.async = true;
    sc.src =
      "https://b.sf-syn.com/badge_js?sf_id=3561893&variant_id=" + variantId;
    const p = document.getElementsByTagName("script")[0];
    p.parentNode.insertBefore(sc, p);
  };
  useEffect(() => {
    setupTags("sf");
    setupTags("sd");
  }, []);
};

export const useStickyHeader = (headerEl, enableFloatingNavBar) => {
  useEffect(() => {
    if (headerEl) {
      if (enableFloatingNavBar) {
        $(window).on("scroll", (event) => {
          var scroll = $(window).scrollTop();
          if (scroll < 1) {
            $(headerEl).removeClass("sticky");
          } else {
            $(headerEl).addClass("sticky");
          }
        });
      } else {
        $(headerEl).addClass("sticky");
      }
    }
    return () => {
      if (headerEl) $(headerEl).removeClass("sticky");
    };
  }, [headerEl, enableFloatingNavBar]);
};

export const useDataDog = () => {
  const [isDataDogLoaded, setIsDataDogLoaded] = useState(false);
  useEffect(() => {
    try {
      if (!isPixelMixerAccount && !getIsTestAgent() && !isDataDogLoaded) {
        datadogRum.init({
          applicationId: "75e4a7ff-3c59-40cd-b077-1be9adf4c469",
          clientToken: "pub0ff4a629fbffc0a5998f1e1c6afadb6f",
          site: "us5.datadoghq.com",
          service:
            process.env.NEXT_PUBLIC_DATADOG_SERVICE || "pixelmixer-website",
          env: isTestEnv ? "dev" : "prod",
          version: process.env.NEXT_PUBLIC_DATADOG_VERSION,
          sessionSampleRate: 100,
          premiumSampleRate: 100,
          trackUserInteractions: true,
          trackResources: true,
          trackSessionAcrossSubdomains: true,
          defaultPrivacyLevel: "allow",
          allowedTracingUrls: [
            (url) => url.indexOf("pixelmixer.com/api/v1") > 0,
          ],
        });

        datadogRum.startSessionReplayRecording();

        datadogLogs.init({
          clientToken: "pub0ff4a629fbffc0a5998f1e1c6afadb6f",
          site: "us5.datadoghq.com",
          service:
            process.env.NEXT_PUBLIC_DATADOG_SERVICE || "pixelmixer-website",
          env: isTestEnv ? "dev" : "prod",
          version: process.env.NEXT_PUBLIC_DATADOG_VERSION,
          forwardErrorsToLogs: true,
          sessionSampleRate: 100,
          trackSessionAcrossSubdomains: true,
        });

        const deviceAPI = UserAgentUtil.getDeviceApi();
        datadogLogs.setGlobalContextProperty("deviceAPI", deviceAPI);
        datadogLogs.logger.setLevel(isTestEnv ? "debug" : "warn");
        setIsDataDogLoaded(true);
      }
    } catch (e) {
      logError("Unable to init DataDog", e);
    }
  }, []);
};

export function useNonce() {
  return typeof document !== "undefined"
    ? document.body?.dataset?.nonce
    : undefined;
}

export const useIsTestAgent = () => {
  const [isTestAgent, setIsTestAgent] = useState(false);
  if (!isTestAgent && getIsTestAgent()) setIsTestAgent(true);
  return isTestAgent;
};
